import { ITicketPrintViewModel } from '../models/print/ticketPrintViewModel.interface';

export interface ITicketPrintValidationResult {
  isValid: boolean;
  check?: string;
  expected?: number;
  actual?: number;
}

// Business model rounding moves the amounts by cents, so a small relative window keeps the check quiet.
const relativeTolerance = 0.001;
const minimalTolerance = 1;

const validResult: ITicketPrintValidationResult = { isValid: true };

const isWithinTolerance = (expected: number, actual: number): boolean =>
  Math.abs(expected - actual) <= Math.max(minimalTolerance, Math.abs(expected) * relativeTolerance);

/**
 * Verifies that the amounts on the slip can be derived from the stake and the odds printed next to them.
 * A mismatch means the model carries amounts that do not belong to this ticket.
 */
export function validateTicketPrintModel(ticketPrint: ITicketPrintViewModel): ITicketPrintValidationResult {
  if (!ticketPrint || ticketPrint.isFreeBet) {
    return validResult;
  }

  const totalStake = ticketPrint.totalStake;
  const totalOdd = ticketPrint.totalOdd;
  const numOfCombinations = ticketPrint.numOfCombinations;
  const possibleWinWithoutBonus = ticketPrint.possibleWinWithoutBonus;
  const possibleWin = ticketPrint.possibleWin;

  if (!(totalStake > 0) || !(totalOdd > 0) || !(numOfCombinations > 0) || !(possibleWinWithoutBonus > 0)) {
    return validResult;
  }

  // Winnings cut down to the payout cap no longer follow from the odds, so they cannot be verified this way.
  if (ticketPrint.maxPayout > 0 && possibleWin >= ticketPrint.maxPayout) {
    return validResult;
  }

  const expectedWinWithoutBonus = (totalStake / numOfCombinations) * totalOdd;
  if (!isWithinTolerance(expectedWinWithoutBonus, possibleWinWithoutBonus)) {
    return {
      isValid: false,
      check: 'possibleWinWithoutBonus',
      expected: expectedWinWithoutBonus,
      actual: possibleWinWithoutBonus
    };
  }

  const expectedPossibleWin = possibleWinWithoutBonus + (ticketPrint.bonus ?? 0);
  if (!isWithinTolerance(expectedPossibleWin, possibleWin)) {
    return { isValid: false, check: 'possibleWin', expected: expectedPossibleWin, actual: possibleWin };
  }

  if (ticketPrint.possibleWinWithoutTax > 0) {
    const expectedWinWithoutTax = possibleWin - (ticketPrint.taxOnWinning ?? 0);
    if (!isWithinTolerance(expectedWinWithoutTax, ticketPrint.possibleWinWithoutTax)) {
      return {
        isValid: false,
        check: 'possibleWinWithoutTax',
        expected: expectedWinWithoutTax,
        actual: ticketPrint.possibleWinWithoutTax
      };
    }
  }

  return validResult;
}
